import Image from 'next/image';
import { useRouter } from 'next/router';

import { Box, Button, Card, Heading, Icon, Text } from '@zillow/constellation-10';
import { IconPanorama360Filled, IconSmartphoneFilled } from '@zillow/constellation-icons';

import { useGetListingDetails } from '../../../../hooks/queries/properties';

import TourAddress from '../../../../../srcLegacy/shared/images/TourAddress.svg';

type ActionCardProps = {
  detailedIcon?: JSX.Element;
  iconSrc?: string;
  iconAlt?: string;
  title: string;
  description: string;
  cta: string;
  url: string;
};

const ActionCard = ({ detailedIcon, iconSrc, iconAlt, title, description, cta, url }: ActionCardProps) => (
  <Card tone="neutral" css={{ display: 'flex', flexDirection: { base: 'column', md: 'row' }, alignItems: 'center' }}>
    {iconSrc && iconAlt ? <Image src={iconSrc} alt={iconAlt} /> : null}
    {detailedIcon ? <Box aria-hidden="true">{detailedIcon}</Box> : null}
    <Box
      css={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: { base: '{spacing.400} 0', md: '0 {spacing.400}' },
        textAlign: { base: 'center', md: 'left' },
      }}
    >
      <Text textStyle="body-sm-bold" css={{ color: 'text.accent.brand.hero' }}>
        {title}
      </Text>
      <Text>{description}</Text>
    </Box>
    <Button
      tone="brand"
      emphasis="outlined"
      asChild
      fluid
      css={{
        flexShrink: 0,
        md: {
          width: 'auto',
        },
      }}
    >
      <a href={url} target="_blank" rel="noopener noreferrer">
        {cta}
      </a>
    </Button>
  </Card>
);

const EmptyTours = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box css={{ paddingBlock: { base: '200', md: '400' } }}>
        <Box css={{ width: '100%', maxWidth: '440px', textAlign: 'center' }}>
          <Heading level={3} textStyle="heading-xs">
            Show off your property with a 3D tour
          </Heading>
        </Box>
      </Box>

      {window.innerHeight > 800 && (
        <Box css={{ paddingBlock: { base: '200', md: '400' } }}>
          <Image src={TourAddress} alt="Tour address" />
        </Box>
      )}

      <Box css={{ paddingBlock: { base: '200', md: '400' } }}>
        <ActionCard
          detailedIcon={<Icon size="md" css={{ color: 'icon.subtle' }} render={<IconSmartphoneFilled />} />}
          title="Create a tour"
          description="Create a tour for free with the Zillow 3D Home app"
          cta="Download app"
          url="https://www.zillow.com/marketing/3d-home/"
        />
      </Box>

      {listingDetails?.isMultifamily && (
        <Box css={{ paddingBlock: { base: '200', md: '400' } }}>
          <ActionCard
            detailedIcon={<Icon size="md" css={{ color: 'icon.subtle' }} render={<IconPanorama360Filled />} />}
            title="Hire a photographer"
            description="Impress renters with professionally shot 3D tours"
            cta="Explore options"
            url="https://showingtimeplus.com/solutions/zillow-media-experts/"
          />
        </Box>
      )}
    </Box>
  );
};

export default EmptyTours;
